import { diffWords } from "diff";
import { useMemo } from "react";

/** Inline word-level diff for prose content. */
export function ProseDiff({
  oldText,
  newText,
  layout = "inline",
  oldLabel = "Before",
  newLabel = "After",
}: {
  oldText: string;
  newText: string;
  layout?: "inline" | "side-by-side";
  oldLabel?: string;
  newLabel?: string;
}) {
  const parts = useMemo(() => diffWords(oldText, newText), [oldText, newText]);

  if (layout === "side-by-side") {
    return (
      <div className="grid gap-3 md:grid-cols-2">
        <section className="min-w-0">
          <h4 className="mb-2 text-xs font-medium text-muted-foreground">
            {oldLabel}
          </h4>
          <div className="text-sm leading-relaxed whitespace-pre-wrap">
            {parts.map((part, i) =>
              part.added ? null : part.removed ? (
                <del
                  key={i}
                  className="rounded-sm bg-red-100 px-0.5 text-red-800 dark:bg-red-900/30 dark:text-red-200"
                >
                  {part.value}
                </del>
              ) : (
                <span key={i}>{part.value}</span>
              ),
            )}
          </div>
        </section>
        <section className="min-w-0 border-t pt-3 md:border-t-0 md:border-l md:pt-0 md:pl-3">
          <h4 className="mb-2 text-xs font-medium text-muted-foreground">
            {newLabel}
          </h4>
          <div className="text-sm leading-relaxed whitespace-pre-wrap">
            {parts.map((part, i) =>
              part.removed ? null : part.added ? (
                <ins
                  key={i}
                  className="rounded-sm bg-green-100 px-0.5 text-green-900 no-underline dark:bg-green-900/30 dark:text-green-200"
                >
                  {part.value}
                </ins>
              ) : (
                <span key={i}>{part.value}</span>
              ),
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="text-sm leading-relaxed whitespace-pre-wrap">
      {parts.map((part, i) => {
        if (part.added) {
          return (
            <ins
              key={i}
              className="rounded-sm bg-green-100 text-green-900 no-underline px-0.5 dark:bg-green-900/30 dark:text-green-200"
            >
              {part.value}
            </ins>
          );
        }

        if (part.removed) {
          return (
            <del
              key={i}
              className="rounded-sm bg-red-100 text-red-800 px-0.5 dark:bg-red-900/30 dark:text-red-200"
            >
              {part.value}
            </del>
          );
        }

        return <span key={i}>{part.value}</span>;
      })}
    </div>
  );
}
